import { z } from "zod";

/**
 * Skills (ARCHITECTURE §6.10): a written procedure the agent reads when a task
 * matches it. Each is a folder with a `SKILL.md`; the catalogue goes into the
 * prompt and the body is read on demand, as in the prototype.
 */

/** Folder name for skills every bot follows; never a real agent id. */
export const SHARED_SKILLS_OWNER = "_shared";

export const SkillSchema = z.object({
  /** The folder name, and the id every route uses. */
  id: z.string().min(1),
  /** The agent that owns it, or `_shared` for the ones every bot follows. */
  agentId: z.string().min(1),
  name: z.string().min(1),
  /** When to reach for it; this is the line the agent chooses on. */
  description: z.string(),
  body: z.string(),
  /** Absolute path to the SKILL.md, which is also what the prompt shows. */
  path: z.string().min(1),
  updatedAt: z.number().int().nonnegative(),
}).strict();
export type Skill = z.infer<typeof SkillSchema>;

export const CreateSkillRequestSchema = z.object({
  name: z.string().min(1),
  description: z.string().default(""),
  body: z.string().min(1),
}).strict();
export type CreateSkillRequest = z.infer<typeof CreateSkillRequestSchema>;

export const UpdateSkillRequestSchema = z.object({
  name: z.string().min(1).optional(),
  description: z.string().optional(),
  body: z.string().min(1).optional(),
}).strict();
export type UpdateSkillRequest = z.infer<typeof UpdateSkillRequestSchema>;

/** GET /agents/:id/skills. `directory` is where they live, so the user can drop one in by hand. */
export const SkillListResponseSchema = z.object({
  skills: z.array(SkillSchema),
  directory: z.string().min(1),
}).strict();
export type SkillListResponse = z.infer<typeof SkillListResponseSchema>;

/** One bot in GET /skills: its id and name, and the skills that belong to it. */
export const SkillOwnerListSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  skills: z.array(SkillSchema),
}).strict();
export type SkillOwnerList = z.infer<typeof SkillOwnerListSchema>;

/** GET /skills: shared skills, then each bot's own. */
export const WorkspaceSkillListResponseSchema = z.object({
  shared: z.array(SkillSchema),
  /** Where shared skills live, so the user can drop one in by hand. */
  sharedDirectory: z.string().min(1),
  agents: z.array(SkillOwnerListSchema),
}).strict();
export type WorkspaceSkillListResponse = z.infer<typeof WorkspaceSkillListResponseSchema>;
